// src/features/rrhh/empleados/pages/EmpleadosPage.jsx
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, IconButton, Pagination, Stack, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import GroupAddIcon from "@mui/icons-material/GroupAdd";

import EmpleadosFilters from "../components/EmpleadosFilters";
import EmpleadosTable from "../components/EmpleadosTable";
import EmpleadoFichaAccordion from "../components/EmpleadoFichaAccordion";
import EmpleadosCargaMasivaModal from "../components/EmpleadosCargaMasivaModal";

import {
  getAllEmpleados,
  getEmpleadoFicha,
  patchEmpleado,
  createEmpleado,
  getSucursales,
  importEmpleadosCsv,
} from "../api/empleadosApi";
import { descargarLegajoPdf } from "../api/empleadosDocumentosApi";
import { getAreas } from "../api/areasApi";
import { getRoles } from "../api/rolesApi";

const PAGE_SIZE = 50;

function unwrapHttp(res) {
  if (res && typeof res === "object" && "data" in res) return res.data;
  return res;
}

function formatDateAny(v) {
  if (!v) return "-";
  const s = String(v).trim();

  if (s.includes("/")) return s;

  const datePart = s.split("T")[0];
  const m = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;

  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;

  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function errorToMessage(err) {
  return (
    err?.response?.data?.detail ||
    err?.response?.data?.message ||
    err?.message ||
    "Error al cargar empleados."
  );
}

function prettySituacion(v) {
  const s = String(v ?? "").toUpperCase();
  if (s === "FORMAL") return "Formal";
  if (s === "INFORMAL") return "Informal";
  return v ?? "-";
}

function prettyEstado(v) {
  const s = String(v ?? "").toUpperCase();
  if (s === "ACTIVE") return "Activo";
  if (s === "INACTIVE") return "Inactivo";
  return v ?? "-";
}

function mapContratoToStatusQuery(value) {
  if (value === "Activo") return "ACTIVE";
  if (value === "Inactivo") return "INACTIVE";
  return undefined;
}

function mapSituacionToContractQuery(value) {
  if (value === "Formal") return "FORMAL";
  if (value === "Informal") return "INFORMAL";
  return undefined;
}

function normalizeEmpleado(e) {
  if (!e) return null;

  const branchNames = (e.employeeBranches || [])
    .filter((row) => !row.deletedAt && (!row.toAt || new Date(row.toAt) >= new Date()))
    .map((row) => row.branch?.name)
    .filter(Boolean);

  const areaNames = (e.employeeAreas || [])
    .filter((row) => !row.deletedAt)
    .map((row) => row.area?.name)
    .filter(Boolean);

  const roleNames = (e.employeeRoles || [])
    .filter((row) => !row.deletedAt)
    .map((row) => row.role?.name)
    .filter(Boolean);

  return {
    ...e,
    id: e.id,
    nombre: e.fullName || "-",
    dni: e.dni || "-",
    fechaAlta: formatDateAny(e.hireDate),
    situacion: prettySituacion(e.contractName),
    contrato: prettyEstado(e.status),

    sucursales: [...new Set(branchNames)],
    areas: [...new Set(areaNames)],
    roles: [...new Set(roleNames)],

    sucursal: [...new Set(branchNames)].join(", ") || "-",
    area: [...new Set(areaNames)].join(", ") || "-",
    rol: [...new Set(roleNames)].join(", ") || "-",

    fechaVencimiento: formatDateAny(e.contractEndDate),
  };
}

function Pill({ children, sx }) {
  return (
    <Box
      sx={{
        px: 2.5,
        py: 1.1,
        bgcolor: "secondary.50",
        borderRadius: "33px",
        boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function CardShell({ children, sx }) {
  return (
    <Box
      sx={{
        p: 2,
        bgcolor: "neutral.300",
        borderRadius: "30px",
        boxShadow: "inset 0px 0px 4px rgba(117, 117, 138, 0.5)",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export default function EmpleadosPage() {
  const navigate = useNavigate();

  const [contrato, setContrato] = useState("Todos");
  const [situacion, setSituacion] = useState("Todos");
  const [sucursal, setSucursal] = useState("Todas");
  const [area, setArea] = useState("Todas");
  const [rol, setRol] = useState("Todos");
  const [searchTabla, setSearchTabla] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [employeesError, setEmployeesError] = useState("");

  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [openCargaMasiva, setOpenCargaMasiva] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);

  const [openAgregar, setOpenAgregar] = useState(false);
  const [openEditar, setOpenEditar] = useState(false);
  const [selectedEmployeeFicha, setSelectedEmployeeFicha] = useState(null);
  const [downloadingLegajo, setDownloadingLegajo] = useState(false);

  const [catalogSucursales, setCatalogSucursales] = useState([]);
  const [catalogAreas, setCatalogAreas] = useState([]);
  const [catalogRoles, setCatalogRoles] = useState([]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(searchTabla.trim());
    }, 300);

    return () => clearTimeout(timeout);
  }, [searchTabla]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, contrato, situacion, sucursal, area, rol]);

  const fetchEmployees = useCallback(async () => {
    setLoadingEmployees(true);
    setEmployeesError("");

    try {
      const res = await getAllEmpleados({
        page,
        limit: PAGE_SIZE,
        search: debouncedSearch || undefined,
        status: mapContratoToStatusQuery(contrato),
        contractName: mapSituacionToContractQuery(situacion),
        branchName: sucursal !== "Todas" ? sucursal : undefined,
        areaName: area !== "Todas" ? area : undefined,
        roleName: rol !== "Todos" ? rol : undefined,
      });

      const data = unwrapHttp(res);

      const list = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data)
        ? data
        : [];

      const normalized = list.map(normalizeEmpleado).filter((x) => x?.id != null);

      setEmployees(normalized);
      setTotalItems(data?.meta?.totalItems ?? normalized.length);
      setTotalPages(data?.meta?.totalPages ?? (normalized.length > 0 ? 1 : 0));
    } catch (err) {
      setEmployees([]);
      setTotalItems(0);
      setTotalPages(0);
      setEmployeesError(errorToMessage(err));
    } finally {
      setLoadingEmployees(false);
      setInitialLoading(false);
    }
  }, [page, debouncedSearch, contrato, situacion, sucursal, area, rol]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  useEffect(() => {
    (async () => {
      try {
        const [branchesRes, areasRes, rolesRes] = await Promise.all([
          getSucursales().catch(() => []),
          getAreas().catch(() => []),
          getRoles().catch(() => []),
        ]);

        const toList = (raw) => {
          if (Array.isArray(raw)) return raw;
          if (Array.isArray(raw?.data)) return raw.data;
          if (Array.isArray(raw?.items)) return raw.items;
          return [];
        };

        setCatalogSucursales(toList(branchesRes));
        setCatalogAreas(toList(areasRes));
        setCatalogRoles(toList(rolesRes));
      } catch {
        //
      }
    })();
  }, []);

  const selectedEmployee = useMemo(
    () => employees.find((employee) => String(employee.id) === String(selectedEmployeeId)) || null,
    [employees, selectedEmployeeId]
  );

  const sucursalOptions = useMemo(() => {
    const names = catalogSucursales.map((b) => b.name || b.nombre).filter(Boolean).sort();
    return ["Todas", ...names];
  }, [catalogSucursales]);

  const areaOptions = useMemo(() => {
    const names = catalogAreas.map((a) => a.name || a.nombre).filter(Boolean).sort();
    return ["Todas", ...names];
  }, [catalogAreas]);

  const rolOptions = useMemo(() => {
    const names = catalogRoles.map((r) => r.name || r.nombre).filter(Boolean).sort();
    return ["Todos", ...names];
  }, [catalogRoles]);

  async function handleEditEmployee(id) {
    setSelectedEmployeeId(id);
    setOpenEditar(true);
    setSelectedEmployeeFicha(null);

    try {
      const fichaRes = await getEmpleadoFicha(id);
      setSelectedEmployeeFicha(unwrapHttp(fichaRes));
    } catch {
      setSelectedEmployeeFicha(null);
    }
  }

  const handleDescargarLegajo = useCallback(async () => {
    const empleadoId = selectedEmployeeFicha?.id ?? selectedEmployee?.id ?? selectedEmployeeId;
    if (!empleadoId) return;

    setDownloadingLegajo(true);
    try {
      await descargarLegajoPdf(empleadoId);
    } finally {
      setDownloadingLegajo(false);
    }
  }, [selectedEmployeeFicha, selectedEmployee, selectedEmployeeId]);

  async function refreshGoingToFirstPage() {
    if (page !== 1) {
      setPage(1);
      return;
    }
    await fetchEmployees();
  }

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
        <IconButton
          onClick={() => navigate("/rrhh/dashboard")}
          sx={{
            width: 36,
            height: 36,
            bgcolor: "secondary.500",
            "&:hover": { bgcolor: "secondary.600" },
          }}
        >
          <ArrowBackIcon sx={{ color: "logoblanco", fontSize: 32 }} />
        </IconButton>

        <Stack sx={{ flex: 1, alignItems: "center", pr: "48px" }}>
          <Pill sx={{ bgcolor: "secondary.200" }}>
            <Typography sx={{ fontSize: 18, fontWeight: 600, color: "primary.900" }}>
              Nómina
            </Typography>
          </Pill>
        </Stack>
      </Stack>

      <CardShell>
        <Stack spacing={1.5}>
          <Stack
            direction="row"
            spacing={1.2}
            sx={{
              justifyContent: "flex-end",
              flexWrap: "wrap",
            }}
          >
            <Button
              onClick={() => setOpenCargaMasiva(true)}
              startIcon={<GroupAddIcon />}
              sx={{
                alignSelf: { xs: "flex-end", xl: "auto" },
                bgcolor: "secondary.500",
                color: "logoblanco",
                px: 2.2,
                py: 1,
                borderRadius: "33px",
                boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
                textTransform: "none",
                fontWeight: 700,
                fontSize: 14,
                "&:hover": { bgcolor: "secondary.600" },
              }}
            >
              Carga masiva
            </Button>

            <Button
              onClick={() => setOpenAgregar(true)}
              startIcon={<PersonAddAlt1Icon />}
              sx={{
                alignSelf: { xs: "flex-end", xl: "auto" },
                bgcolor: "secondary.500",
                color: "logoblanco",
                px: 2.2,
                py: 1,
                borderRadius: "33px",
                boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
                textTransform: "none",
                fontWeight: 700,
                fontSize: 14,
                "&:hover": { bgcolor: "secondary.600" },
              }}
            >
              Agregar personal
            </Button>
          </Stack>

          <Stack
            direction={{ xs: "column", xl: "row" }}
            spacing={1.5}
            sx={{ alignItems: { xs: "stretch", xl: "center" } }}
          >
            <Box sx={{ flex: 1 }}>
              <EmpleadosFilters
                contrato={contrato}
                situacion={situacion}
                sucursal={sucursal}
                area={area}
                rol={rol}
                search={searchTabla}
                sucursalOptions={sucursalOptions}
                areaOptions={areaOptions}
                rolOptions={rolOptions}
                onChangeContrato={setContrato}
                onChangeSituacion={setSituacion}
                onChangeSucursal={setSucursal}
                onChangeArea={setArea}
                onChangeRol={setRol}
                onChangeSearch={setSearchTabla}
              />
            </Box>
          </Stack>

          {initialLoading && loadingEmployees && (
            <Typography sx={{ px: 1, fontSize: 13, fontWeight: 600, color: "primary.900" }}>
              Cargando empleados…
            </Typography>
          )}

          {!!employeesError && (
            <Box
              sx={{
                px: 2,
                py: 1.2,
                borderRadius: "18px",
                bgcolor: "secondary.700",
                boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.35)",
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "logoblanco" }}>
                No se pudieron cargar empleados
              </Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: "logoblanco", opacity: 0.95 }}>
                {employeesError}
              </Typography>
            </Box>
          )}

          <EmpleadosTable
            rows={employees}
            selectedId={selectedEmployeeId}
            onSelect={setSelectedEmployeeId}
            onEdit={handleEditEmployee}
            height={{ xs: 250, lg: 360, xl: "calc(100vh - 360px)" }}
          />

          {!employeesError && (
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.2}
              sx={{
                alignItems: { xs: "flex-start", md: "center" },
                justifyContent: "space-between",
                pt: 0.5,
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "primary.900" }}>
                Mostrando {employees.length} de {totalItems} empleados
              </Typography>

              <Pagination
                page={page}
                count={Math.max(totalPages, 1)}
                onChange={(_, value) => setPage(value)}
                shape="rounded"
                color="primary"
                disabled={loadingEmployees || totalPages <= 1}
              />
            </Stack>
          )}
        </Stack>
      </CardShell>

      <EmpleadoFichaAccordion
        open={openAgregar}
        onClose={() => setOpenAgregar(false)}
        mode="create"
        onCreate={async (formData) => {
          await createEmpleado(formData);
          await refreshGoingToFirstPage();
          setOpenAgregar(false);
        }}
      />

      <EmpleadoFichaAccordion
        open={openEditar}
        onClose={() => {
          setOpenEditar(false);
          setSelectedEmployeeFicha(null);
        }}
        employee={selectedEmployeeFicha ?? selectedEmployee}
        onSave={async (id, data) => {
          await patchEmpleado(id, data);
          await fetchEmployees();
          try {
            const res = await getEmpleadoFicha(id);
            setSelectedEmployeeFicha(unwrapHttp(res));
          } catch {}
        }}
        onDescargarLegajo={handleDescargarLegajo}
        downloadingLegajo={downloadingLegajo}
      />

      <EmpleadosCargaMasivaModal
        open={openCargaMasiva}
        onClose={() => setOpenCargaMasiva(false)}
        onConfirm={async (file) => {
          await importEmpleadosCsv(file);
          await refreshGoingToFirstPage();
        }}
      />
    </Stack>
  );
}